/**
 * Utility function to parse regularHours from API format to form format
 * 
 * API format: { "MONDAY": "07:00 - 18:00", "TUESDAY": "07:00 - 18:00", ... }
 * Form format: { "Monday": { status: "open", hours: [{ start: "07:00", end: "18:00" }] }, ... }
 */

export interface OpeningHoursDay {
  status: 'open' | 'closed' | '24hrs' | 'split';
  hours: Array<{ start: string; end: string }>;
}

export type OpeningHours = Record<string, OpeningHoursDay>;

export interface RegularHours {
  [key: string]: string;
}

/**
 * Maps API day names (uppercase) to form day names (capitalized)
 */
const DAY_NAME_MAP: Record<string, string> = {
  MONDAY: 'Monday',
  TUESDAY: 'Tuesday',
  WEDNESDAY: 'Wednesday',
  THURSDAY: 'Thursday',
  FRIDAY: 'Friday',
  SATURDAY: 'Saturday',
  SUNDAY: 'Sunday'
};

/**
 * All days of the week in the expected form format
 */
const ALL_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/**
 * Parses regularHours from API format to form format
 * 
 * @param regularHours - The regularHours object from the API (e.g., { "MONDAY": "07:00 - 18:00" })
 * @returns An object with all days, where days with hours are marked as "open" and others as "closed"
 */
export const parseOpeningHours = (regularHours: RegularHours | null | undefined): OpeningHours => {
  // Initialize all days as closed
  const openingHours = ALL_DAYS.reduce(
    (acc, day) => ({
      ...acc,
      [day]: { status: 'closed' as const, hours: [] }
    }),
    {} as OpeningHours
  );

  // Update days that have hours in regularHours
  if (regularHours) {
    Object.entries(regularHours).forEach(([apiDay, hours]) => {
      const formDay = DAY_NAME_MAP[apiDay];
      if (formDay && typeof hours === 'string' && hours.trim()) {
        const [start, end] = hours.split(' - ');
        if (start && end) {
          openingHours[formDay] = {
            status: 'open',
            hours: [{ start: start.trim(), end: end.trim() }]
          };
        }
      }
    });
  }

  return openingHours;
};

